import { useEffect, useState } from 'react';

/**
 * Per-page Matrix / Neon skin toggle.
 * - `storageKey`: separate localStorage key per page (patron / youngbuzz)
 * - `defaultMode`: 'neon' | 'matrix' (defaults to neon)
 * - When 'matrix': applies `.matrix-skin` to <body> AND renders a bright-green rain canvas
 * - When 'neon': removes matrix classes, hides rain
 */
export default function LocalMatrixToggle({ storageKey = 'nb-matrix-mode', defaultMode = 'neon' }) {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(storageKey) || defaultMode;
    } catch { return defaultMode; }
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, mode); } catch { /* ignore */ }
    if (mode === 'matrix') {
      document.body.classList.add('matrix-skin');
      document.body.classList.remove('matrix-off');
    } else {
      document.body.classList.remove('matrix-skin');
      document.body.classList.add('matrix-off');
    }
    return () => {
      // Cleanup when unmounting so other pages don't inherit
      document.body.classList.remove('matrix-skin');
      document.body.classList.remove('matrix-off');
    };
  }, [mode, storageKey]);

  const isMatrix = mode === 'matrix';

  return (
    <button
      data-testid="local-matrix-toggle"
      onClick={() => setMode(isMatrix ? 'neon' : 'matrix')}
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        border: `1px solid ${isMatrix ? '#00FF41' : '#9D00FF'}`,
        borderRadius: 8,
        padding: '10px 16px',
        fontFamily: 'Orbitron, sans-serif',
        fontSize: 10,
        fontWeight: 700,
        color: isMatrix ? '#00FF41' : '#9D00FF',
        cursor: 'pointer',
        letterSpacing: 1,
        boxShadow: `0 0 12px ${isMatrix ? 'rgba(0,255,65,0.4)' : 'rgba(157,0,255,0.4)'}`,
        transition: 'all 0.3s',
      }}
    >
      {isMatrix ? 'MATRIX: ON' : 'NEON: ON'}
    </button>
  );
}

export function useMatrixMode(storageKey = 'nb-matrix-mode', defaultMode = 'neon') {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(storageKey) || defaultMode; }
    catch { return defaultMode; }
  });
  useEffect(() => {
    const iv = setInterval(() => {
      try {
        const cur = localStorage.getItem(storageKey) || defaultMode;
        setMode(cur);
      } catch { /* ignore */ }
    }, 400);
    return () => clearInterval(iv);
  }, [storageKey, defaultMode]);
  return mode;
}
